import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardHeader, CardContent, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AcceptInviteForm = ({ token }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [team, setTeam] = useState('MG&BK Fodbold');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [inviteInfo, setInviteInfo] = useState(null);
  const [success, setSuccess] = useState(false);

  const TEAMS = ['Rock Under Broen', 'MG&BK Gymnastik', 'MG&BK Fodbold'];

  // Validate invitation token on component mount
  useEffect(() => {
    const validateToken = async () => {
      try {
        // Check if invitation is valid by attempting to get info
        // For now, we'll just show the form since we don't have a validation endpoint
        setInviteInfo({ email: 'Din email adresse' }); // Placeholder
      } catch (error) {
        setError('Ugyldig eller udløbet invitation');
      }
    };

    if (token) {
      validateToken();
    } else {
      setError('Ingen invitation token fundet');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email || !name || !team || !password || !confirmPassword) {
      setError('Udfyld venligst alle felter');
      return;
    }

    if (!email.includes('@')) {
      setError('Indtast en gyldig email adresse');
      return;
    }

    if (name.trim().length < 2) {
      setError('Indtast dit fulde navn');
      return;
    }

    if (password !== confirmPassword) {
      setError('Adgangskoder matcher ikke');
      return;
    }

    if (password.length < 8) {
      setError('Adgangskoden skal være mindst 8 tegn');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/accept-invite`, {
        token,
        email,
        name: name.trim(),
        organization: team,  // Use team value for both organization and team
        team,
        password
      });

      setSuccess(true);
      toast.success('Konto oprettet succesfuldt! Du kan nu logge ind.');
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        window.location.href = '/';
      }, 3000);

    } catch (error) {
      console.error('Accept invite error:', error.response?.data);
      
      let errorMsg = 'Kunne ikke oprette konto';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        // Handle different error formats
        if (typeof detail === 'string') {
          errorMsg = detail;
        } else if (Array.isArray(detail)) {
          // Handle validation errors array
          errorMsg = detail.map(err => {
            if (typeof err === 'string') return err;
            if (err.msg) return err.msg;
            if (err.message) return err.message;
            return 'Valideringsfejl';
          }).join(', ');
        } else if (detail.msg) {
          errorMsg = detail.msg;
        } else if (detail.message) {
          errorMsg = detail.message;
        } else {
          errorMsg = 'Ukendt fejl opstod';
        }
      }
      
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center px-4">
        <Card className="w-full max-w-md shadow-xl">
          <CardHeader className="text-center pb-4">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            <CardTitle className="text-2xl font-bold text-green-600">
              Konto Oprettet!
            </CardTitle>
            <p className="text-slate-600 text-sm mt-2">
              Din konto er blevet oprettet succesfuldt. Du bliver omdirigeret til login siden...
            </p>
          </CardHeader>
          <CardContent className="p-6">
            <Button 
              onClick={() => window.location.href = '/'}
              className="w-full bg-green-600 hover:bg-green-700"
            >
              Gå til Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center px-4">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center pb-4">
          <div className="flex justify-center mb-4">
            <div className="flex items-center gap-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_roomreserve-46/artifacts/ogw5o67w_cropped-Logo-med-hvid-baggrund-90x90.png" 
                alt="Gymnastik Logo" 
                style={{ height: '48px', width: '48px' }}
                className="object-contain"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_roomreserve-46/artifacts/pzd8lr16_MG_%26_BK_logo_136.png" 
                alt="MG & BK Logo" 
                className="h-12 object-contain"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_roomreserve-46/artifacts/h1gpmvyf_RUB_logo_Siden_1989_CMYK.jpg" 
                alt="Rock Under Broen Logo" 
                className="h-12 object-contain"
              />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold bg-gradient-to-r from-slate-700 to-slate-900 bg-clip-text text-transparent">
            Accepter Invitation
          </CardTitle>
          <p className="text-slate-600 text-sm mt-2">
            Udfyld dine oplysninger og opret din adgangskode for at få adgang til booking systemet
          </p>
        </CardHeader>
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Alert className="border-red-200 bg-red-50">
                <AlertDescription className="text-red-800">{error}</AlertDescription>
              </Alert>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="email">Email Adresse</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="din@email.dk"
                required
                disabled={loading}
              />
              <p className="text-xs text-slate-600">
                Indtast den email adresse som invitationen blev sendt til
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="name">Fulde Navn</Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Dit fulde navn"
                required
                disabled={loading}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="team">Team</Label>
              <Select value={team} onValueChange={setTeam} disabled={loading}>
                <SelectTrigger>
                  <SelectValue placeholder="Vælg dit team" />
                </SelectTrigger>
                <SelectContent>
                  {TEAMS.map(teamOption => (
                    <SelectItem key={teamOption} value={teamOption}>{teamOption}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-600">
                {team === 'Rock Under Broen' ? 'Dit team kan booke alle lokaler inklusiv "Rocken"' : 'Dit team kan booke alle lokaler undtagen "Rocken"'}
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Adgangskode</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mindst 8 tegn"
                required
                disabled={loading}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Bekræft Adgangskode</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Gentag adgangskoden"
                required
                disabled={loading}
              />
            </div>
            
            <Button 
              type="submit" 
              className="w-full bg-blue-600 hover:bg-blue-700"
              disabled={loading}
            >
              {loading ? 'Opretter konto...' : 'Opret Konto'}
            </Button>
          </form>
          
          <div className="mt-6 p-4 bg-slate-50 rounded-lg border">
            <p className="text-xs text-slate-600 text-center">
              Efter oprettelse af din konto kan du logge ind og få adgang til mødelokale booking systemet.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};